import styled, { keyframes } from "styled-components";

export const PageHeader = styled.header`
  background: var(--surface);
  border-bottom: 1px solid var(--border-subtle);
  padding: 1rem 0 0.875rem;
`;

export const Breadcrumb = styled.nav`
  font-size: 0.8125rem;
  color: var(--text-label);
  margin-bottom: 0.25rem;

  a {
    color: var(--primary);
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
  span { margin: 0 0.375rem; }
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

export const BackButton = styled.button`
  background: none;
  border: none;
  padding: 0.25rem;
  color: var(--text-label);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  border-radius: 0.375rem;
  transition: color 0.15s ease, background 0.15s ease;
  font-size: 1.25rem;

  &:hover { color: var(--text-secondary); background: var(--surface-3); }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
`;

export const PageTitle = styled.h1`
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const DateBadge = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-muted);
`;

export const RuleHint = styled.p`
  font-size: 0.8125rem;
  color: var(--text-muted);
  margin: 0.375rem 0 0;

  i { color: var(--primary); margin-right: 0.25rem; }
`;

/* ── Summary bar ─────────────────────────────── */
export const SummaryBar = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
  padding: 0.75rem 1rem;
  background: var(--surface-2);
  border: 1px solid var(--border-subtle);
  border-radius: var(--border-radius-sm);
  margin-bottom: 1rem;
`;

export const SummaryItem = styled.div`
  .s-label {
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-label);
    margin-bottom: 0.125rem;
  }
  .s-value {
    font-size: 1.125rem;
    font-weight: 800;
    color: var(--primary);
  }
`;

/* ── Card por instituição ─────────────────────── */
export const CompanyCard = styled.section`
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow-sm);
  margin-bottom: 1rem;
  overflow: hidden;
`;

export const CompanyHeader = styled.header`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid var(--border-subtle);
  background: var(--surface-2);

  > i {
    font-size: 1.25rem;
    color: var(--primary);
  }
`;

export const CompanyName = styled.h2`
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CompanyTotal = styled.span`
  font-size: 0.9375rem;
  font-weight: 800;
  color: var(--primary);
  white-space: nowrap;
`;

export const CnabButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 700;
  padding: 0.45rem 1rem;
  border-radius: 2rem;
  border: none;
  background: var(--primary);
  color: #fff;
  cursor: pointer;
  transition: background 0.15s ease;
  white-space: nowrap;

  &:hover:not(:disabled) { background: var(--primary-hover); }
  &:disabled { opacity: 0.65; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }

  i { font-size: 1rem; }
`;

export const QuoteTable = styled.table`
  width: 100%;
  margin: 0;
  font-size: 0.8438rem;
  border-collapse: collapse;

  th {
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-label);
    padding: 0.625rem 0.75rem;
    border-bottom: 1px solid var(--border-subtle);
    text-align: left;
    white-space: nowrap;

    &.num { text-align: right; }
  }

  td {
    padding: 0.625rem 0.75rem;
    border-bottom: 1px solid var(--border-subtle);
    color: var(--text-secondary);
    vertical-align: middle;

    &.num { text-align: right; white-space: nowrap; }
  }

  tbody tr:last-child td { border-bottom: none; }
  tbody tr:hover td { background: var(--surface-2); }

  .repasse-value {
    font-weight: 700;
    color: var(--primary);
  }

  a {
    color: var(--primary);
    font-weight: 700;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`;

export const StatusTag = styled.span<{ $paid?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.6875rem;
  font-weight: 700;
  padding: 0.125rem 0.5rem;
  border-radius: 2rem;
  white-space: nowrap;
  color: ${({ $paid }) => ($paid ? "#15803d" : "#b45309")};
  background: ${({ $paid }) =>
    $paid ? "rgba(21,128,61,0.1)" : "rgba(180,83,9,0.1)"};

  body.dark & {
    color: ${({ $paid }) => ($paid ? "#4ade80" : "#fbbf24")};
    background: ${({ $paid }) =>
      $paid ? "rgba(74,222,128,0.12)" : "rgba(251,191,36,0.12)"};
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: var(--text-label);
  text-align: center;

  i { font-size: 3rem; margin-bottom: 1rem; opacity: 0.35; }
  p { font-size: 0.9375rem; margin: 0; }
  small { margin-top: 0.25rem; }
`;

const shimmer = keyframes`
  from { background-position: 200% 0; }
  to   { background-position: -200% 0; }
`;

export const Skeleton = styled.span<{ width?: string; height?: string }>`
  display: inline-block;
  width: ${(props) => props.width ?? "100%"};
  height: ${(props) => props.height ?? "1rem"};
  border-radius: 0.375rem;
  background: linear-gradient(
    90deg,
    var(--surface-3) 25%,
    var(--surface-2) 50%,
    var(--surface-3) 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
